import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { BarChart3, BellRing, Building2, FileText, FolderKanban, Layers3, Settings, ShieldCheck, Users } from "lucide-react";

const defaultWelcomeForm = {
  id: null as string | null,
  eyebrow: "Boas-vindas",
  title: "Bem-vindo ao Portal da Jornada do Cliente",
  summary: "Aqui você acompanha seu acesso, encontra os serviços disponíveis e vê as mensagens principais da Sálvia logo na entrada.",
  body:
    "Use a página inicial para abrir seus serviços, retomar tarefas importantes e acompanhar mensagens que ajudam no seu dia a dia.",
  ctaLabel: "Abrir serviços",
  ctaHref: "/portal/servicos",
};

export default function ModuleNucleusEntry() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [welcomeForm, setWelcomeForm] = useState(defaultWelcomeForm);

  const workspaceQuery = trpc.portalAccount.getWorkspace.useQuery(undefined, {
    enabled: !!user,
    staleTime: 30_000,
  });

  const saveWelcomeMutation = trpc.portalAccount.upsertWelcomeItem.useMutation({
    onSuccess: async () => {
      toast.success("Aviso principal salvo com sucesso.");
      await utils.portalAccount.getWorkspace.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Não foi possível salvar o aviso principal.");
    },
  });

  const canManageNotices = Boolean(workspaceQuery.data?.canManageContent);
  const editableWelcomeItem = workspaceQuery.data?.editableWelcomeItem ?? null;

  useEffect(() => {
    if (!editableWelcomeItem) return;

    setWelcomeForm({
      id: editableWelcomeItem.id,
      eyebrow: editableWelcomeItem.eyebrow,
      title: editableWelcomeItem.title,
      summary: editableWelcomeItem.summary,
      body: editableWelcomeItem.body,
      ctaLabel: editableWelcomeItem.ctaLabel ?? "",
      ctaHref: editableWelcomeItem.ctaHref ?? "",
    });
  }, [editableWelcomeItem?.id, editableWelcomeItem?.updatedAt]);

  const entries = useMemo(
    () => [
      {
        key: "panel",
        title: "Painel Administrativo",
        description: "Acompanhe a organização geral da plataforma e siga para as áreas de gestão.",
        icon: BarChart3,
        actionLabel: "Abrir painel",
        onClick: () => setLocation("/channels"),
      },
      {
        key: "channels",
        title: "Canais",
        description: "Gerencie canais parceiros e acompanhe quem atende cada operação.",
        icon: Users,
        actionLabel: "Abrir canais",
        onClick: () => setLocation("/channels"),
      },
      {
        key: "clients",
        title: "Clientes",
        description: "Veja as empresas cadastradas e siga para a gestão de cada uma.",
        icon: Building2,
        actionLabel: "Abrir clientes",
        onClick: () => setLocation("/clients"),
      },
      {
        key: "contracts",
        title: "Contratos",
        description: "Consulte liberações e acompanhe como os serviços estão organizados.",
        icon: FileText,
        actionLabel: "Abrir contratos",
        onClick: () => setLocation("/contratos"),
      },
      {
        key: "services",
        title: "Catálogo de Serviços",
        description: "Confira os serviços disponíveis para cada realidade de atendimento.",
        icon: Settings,
        actionLabel: "Abrir catálogo",
        onClick: () => setLocation("/servicos"),
      },
      {
        key: "users",
        title: "Usuários e Perfis",
        description: "Controle acessos e organize quem pode entrar em cada parte do portal.",
        icon: ShieldCheck,
        actionLabel: "Abrir usuários",
        onClick: () => setLocation("/configuracoes/usuarios"),
      },
      {
        key: "notices",
        title: "Avisos do portal",
        description: "Edite aqui a mensagem principal que aparece na entrada do portal para todos os perfis.",
        icon: BellRing,
        actionLabel: "Editar avisos",
        onClick: () => document.getElementById("avisos-do-portal")?.scrollIntoView({ behavior: "smooth", block: "start" }),
      },
    ],
    [setLocation],
  );

  const handleWelcomeFieldChange = (field: keyof typeof defaultWelcomeForm, value: string | null) => {
    setWelcomeForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSaveWelcome = async () => {
    await saveWelcomeMutation.mutateAsync({
      id: welcomeForm.id,
      eyebrow: welcomeForm.eyebrow,
      title: welcomeForm.title,
      summary: welcomeForm.summary,
      body: welcomeForm.body,
      ctaLabel: welcomeForm.ctaLabel,
      ctaHref: welcomeForm.ctaHref,
      audience: "all",
      status: "published",
      sortOrder: 10,
    });
  };

  return (
    <SalviaLayout title="Administração da Plataforma">
      <div className="space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm lg:p-8">
          <div className="grid gap-6 xl:grid-cols-[1.05fr,0.95fr] xl:items-start">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                <Layers3 className="h-3.5 w-3.5" />
                Administração da Plataforma
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-slate-900">Administração da Plataforma</h1>
                <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                  Aqui ficam os controles de gestão do portal: canais, clientes, contratos, acessos e os avisos gerais que aparecem para quem entra na plataforma.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => setLocation("/portal")}>Voltar ao Portal da Jornada do Cliente</Button>
                <Button onClick={() => setLocation("/contratos")}>Abrir contratos</Button>
              </div>
            </div>

            <div className="rounded-[2rem] bg-slate-950 p-6 text-white">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                <FolderKanban className="h-4 w-4 text-[#C0FF00]" />
                O que esta área concentra
              </div>
              <p className="mt-4 text-2xl font-semibold">Organizar a plataforma e sustentar a operação.</p>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Use esta área para cuidar de cadastros, acessos, contratos, serviços e da mensagem principal que recebe o usuário na entrada do portal.
              </p>

              <div className="mt-6 grid gap-3">
                <div className="rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                  <p className="font-semibold text-white">Gestão</p>
                  <p className="mt-1">Canais, clientes, contratos e catálogo de serviços.</p>
                </div>
                <div className="rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                  <p className="font-semibold text-white">Acessos</p>
                  <p className="mt-1">Usuários, perfis e organização das permissões.</p>
                </div>
                <div className="rounded-3xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                  <p className="font-semibold text-white">Comunicação</p>
                  <p className="mt-1">Aviso principal mostrado na entrada do portal.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {entries.map((entry) => {
            const Icon = entry.icon;
            return (
              <article key={entry.key} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="mt-4 text-lg font-semibold text-slate-900">{entry.title}</h2>
                <p className="mt-2 min-h-24 text-sm leading-6 text-slate-600">{entry.description}</p>
                <Button className="mt-5 w-full" onClick={entry.onClick}>
                  {entry.actionLabel}
                </Button>
              </article>
            );
          })}
        </section>

        {canManageNotices ? (
          <section id="avisos-do-portal">
            <Card className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                      <BellRing className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Mensagem principal do portal</p>
                      <h2 className="mt-1 text-2xl font-semibold text-slate-950">Editar aviso da entrada</h2>
                    </div>
                  </div>
                  <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                    O conteúdo salvo aqui aparece na página inicial do portal. Use uma mensagem curta, clara e útil para orientar quem está entrando.
                  </p>
                </div>
                <Badge variant="secondary" className="h-fit rounded-full px-3 py-1">Exibição na Home</Badge>
              </div>

              {workspaceQuery.isLoading ? (
                <div className="mt-6 grid gap-5 lg:grid-cols-2">
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-full rounded-xl" />
                    <Skeleton className="h-10 w-full rounded-xl" />
                    <Skeleton className="h-32 w-full rounded-2xl" />
                  </div>
                  <div className="space-y-3">
                    <Skeleton className="h-40 w-full rounded-2xl" />
                    <Skeleton className="h-10 w-full rounded-xl" />
                    <Skeleton className="h-10 w-full rounded-xl" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-6 grid gap-5 lg:grid-cols-2">
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="welcome-eyebrow">Rótulo curto</Label>
                        <Input
                          id="welcome-eyebrow"
                          value={welcomeForm.eyebrow}
                          onChange={(event) => handleWelcomeFieldChange("eyebrow", event.target.value)}
                          placeholder="Ex.: Boas-vindas"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="welcome-title">Título</Label>
                        <Input
                          id="welcome-title"
                          value={welcomeForm.title}
                          onChange={(event) => handleWelcomeFieldChange("title", event.target.value)}
                          placeholder="Digite o título do aviso"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="welcome-summary">Resumo</Label>
                        <Textarea
                          id="welcome-summary"
                          value={welcomeForm.summary}
                          onChange={(event) => handleWelcomeFieldChange("summary", event.target.value)}
                          rows={4}
                          placeholder="Resumo curto que aparecerá logo abaixo do título"
                        />
                      </div>
                    </div>

                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="welcome-body">Texto principal</Label>
                        <Textarea
                          id="welcome-body"
                          value={welcomeForm.body}
                          onChange={(event) => handleWelcomeFieldChange("body", event.target.value)}
                          rows={7}
                          placeholder="Mensagem principal que será mostrada na Home"
                        />
                      </div>

                      <div className="grid gap-5 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="welcome-cta-label">Texto do botão</Label>
                          <Input
                            id="welcome-cta-label"
                            value={welcomeForm.ctaLabel}
                            onChange={(event) => handleWelcomeFieldChange("ctaLabel", event.target.value)}
                            placeholder="Ex.: Abrir serviços"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="welcome-cta-href">Destino do botão</Label>
                          <Input
                            id="welcome-cta-href"
                            value={welcomeForm.ctaHref}
                            onChange={(event) => handleWelcomeFieldChange("ctaHref", event.target.value)}
                            placeholder="/portal/servicos"
                          />
                        </div>
                      </div>

                      <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-4 text-sm leading-7 text-slate-600">
                        Prefira uma mensagem simples. Ela deve acolher, orientar e levar a pessoa para o próximo passo com rapidez.
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button onClick={handleSaveWelcome} disabled={saveWelcomeMutation.isPending}>
                      {saveWelcomeMutation.isPending ? "Salvando aviso..." : "Salvar aviso principal"}
                    </Button>
                    <Button variant="outline" onClick={() => setWelcomeForm(defaultWelcomeForm)} disabled={saveWelcomeMutation.isPending}>
                      Restaurar modelo inicial
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </section>
        ) : null}
      </div>
    </SalviaLayout>
  );
}
